import { observer } from 'mobx-react-lite'
import { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { SimpleGrid, Group, Box } from '@mantine/core'
import { FiUpload, FiTrash2, FiImage } from 'react-icons/fi'
import { Photo, Profile } from '../../app/models/profile'
import { useStore } from '../../app/stores/store'
import { toast } from 'react-toastify'
import { AppButton, AppCard, AppIconButton, AppModal, AppText } from '../../app/common/ui'
import PhotoUploadCropModal from './PhotoUploadCropModal'

interface Props {
  profile: Profile;
}

export default observer(function ProfilePhotos ({ profile }: Props) {
  const { profileStore } = useStore()
  const { isCurrentUser, uploadPhoto, setMainPhoto, deletePhoto, uploading, loading } = profileStore
  const [cropFile, setCropFile] = useState<File | null>(null)
  const [photoToDelete, setPhotoToDelete] = useState<Photo | null>(null)

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) setCropFile(acceptedFiles[0])
    },
    []
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'image/jpeg,image/jpg,image/png,image/webp,image/gif',
    maxFiles: 1,
    multiple: false,
    maxSize: 5 * 1024 * 1024,
    disabled: !isCurrentUser,
    onDropRejected: (fileRejections) => {
      const err = fileRejections[0]?.errors[0]
      if (err?.code === 'file-too-large') toast.error('Image must be under 5MB')
      else toast.error(err?.message || 'Invalid image')
    }
  })

  const handleDeleteClick = (photo: Photo) => {
    if (photo.isMain) return
    setPhotoToDelete(photo)
  }

  const handleConfirmDelete = async () => {
    if (photoToDelete) {
      await deletePhoto(photoToDelete)
      setPhotoToDelete(null)
      toast.success('Photo deleted')
    }
  }

  const handleUpload = async (blob: Blob) => {
    await uploadPhoto(blob)
    setCropFile(null)
  }

  const photos = profile.photos ?? []

  return (
    <Box>
      {isCurrentUser && (
        <AppCard
          {...getRootProps()}
          p="xl"
          radius="md"
          withBorder
          sx={{
            cursor: isCurrentUser ? 'pointer' : 'default',
            borderStyle: 'dashed',
            borderWidth: 2,
            backgroundColor: isDragActive ? 'rgba(232,93,4,0.08)' : undefined,
            transition: 'background-color 0.15s ease'
          }}
        >
          <input {...getInputProps()} />
          <Group position="center" spacing="xs">
            <FiUpload size={24} />
            <AppText color="dimmed" size="sm">
              Drop an image here or click to upload
            </AppText>
          </Group>
        </AppCard>
      )}

      {photos.length > 0 && (
        <SimpleGrid cols={4} breakpoints={[{ maxWidth: 'sm', cols: 2 }]} mt="md" spacing="md">
          {photos.map((photo) => (
            <AppCard key={photo.id} radius="md" withBorder sx={{ overflow: 'hidden', position: 'relative' }}>
              <Box
                component="img"
                src={photo.url}
                alt=""
                sx={{ width: '100%', height: 180, objectFit: 'cover', display: 'block' }}
              />
              {isCurrentUser && (
                <Group position="center" spacing="xs" p="xs" sx={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
                  <AppIconButton
                    size="sm"
                    variant={photo.isMain ? 'filled' : 'light'}
                    color={photo.isMain ? 'green' : 'gray'}
                    onClick={() => setMainPhoto(photo)}
                    disabled={photo.isMain || loading}
                  >
                    <FiImage size={14} />
                  </AppIconButton>
                  <AppIconButton
                    size="sm"
                    variant="light"
                    color="red"
                    onClick={() => handleDeleteClick(photo)}
                    disabled={photo.isMain || loading}
                  >
                    <FiTrash2 size={14} />
                  </AppIconButton>
                </Group>
              )}
              {photo.isMain && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    padding: '2px var(--gs-xs)',
                    borderRadius: 4,
                    backgroundColor: 'var(--mantine-color-green-6)',
                    color: 'white',
                    fontSize: 11
                  }}
                >
                  Main
                </Box>
              )}
            </AppCard>
          ))}
        </SimpleGrid>
      )}

      {photos.length === 0 && !isCurrentUser && (
        <AppText color="dimmed" ta="center" py="xl">
          No photos yet
        </AppText>
      )}

      <PhotoUploadCropModal
        file={cropFile}
        open={!!cropFile}
        onClose={() => setCropFile(null)}
        onCrop={handleUpload}
        loading={uploading}
      />

      <AppModal
        opened={!!photoToDelete}
        onClose={() => setPhotoToDelete(null)}
        title="Delete photo"
        withCloseButton
      >
        <AppText size="sm" mb="md">
          Are you sure you want to delete this photo? This action cannot be undone.
        </AppText>
        <Group spacing="xs">
          <AppButton color="red" onClick={handleConfirmDelete} loading={loading}>
            Delete
          </AppButton>
          <AppButton variant="subtle" color="gray" onClick={() => setPhotoToDelete(null)} disabled={loading}>
            Cancel
          </AppButton>
        </Group>
      </AppModal>
    </Box>
  )
})
